import React, { useState, useEffect } from "react";
import { Form, Button, Container, Card, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../config/Supabase";
import { SERVER_URL } from "../../config/api";
import "../../styles/General.css";
import "../../styles/Login.css";
import { trackSignup } from "../../utils/analytics";

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Handle redirect back from Google / Apple OAuth only.
  // Email/password sign-up is handled directly inside handleSignUp() below.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_IN" && session?.user) {
          const provider = session.user.app_metadata?.provider;
          if (provider === "google" || provider === "apple") {
            trackSignup(provider);
            await createStripeCustomer(session.user.id, session.user.email);
            navigate("/");
          }
        }
      }
    );
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createStripeCustomer = async (userId, userEmail) => {
    try {
      const response = await fetch(`${SERVER_URL}/create-customer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, userId }),
      });
      if (!response.ok) {
        const data = await response.json();
        console.error("Stripe customer creation failed:", data.error);
      }
    } catch (err) {
      console.error("Failed to create Stripe customer:", err);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email) || !password.trim()) {
      setError("Both email and password are required and email must be valid.");
      return;
    }

    setLoading(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
      });
      if (signUpError) throw signUpError;

      trackSignup("email");
      await createStripeCustomer(data.user.id, email);
      setMessage(
        "Account created! Check your inbox for a verification email before logging in."
      );
    } catch (err) {
      setError(`Failed to create an account: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const signUpWithOAuth = async (provider) => {
    setError("");
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/signUp`,
      },
    });
    if (oauthError) {
      setError(`Failed to sign up with ${provider === "google" ? "Google" : "Apple"}.`);
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh", height: "600px" }}
    >
      <div className="w-100" style={{ maxWidth: "500px", paddingTop: "60px" }}>
        <Card className="shadow no-border-card" style={{ height: "auto" }}>
          <Card.Body>
            <Card.Title className="login-card-title">
              Create an account
            </Card.Title>
            <div
              className="w-100"
              style={{ maxWidth: "400px", paddingTop: "25px" }}
            ></div>
            <Form onSubmit={handleSignUp}>
              <Form.Group controlId="formBasicEmail">
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Group>
              <div style={{ paddingTop: "20px", paddingBottom: "25px" }}>
                <Form.Group controlId="formBasicPassword">
                  <Form.Control
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Group>
              </div>

              {message && (
                <Alert variant="success" className="mt-3">
                  {message}
                </Alert>
              )}
              {error && (
                <Alert variant="danger" className="mt-3" onClose={() => setError("")} dismissible>
                  {error}
                </Alert>
              )}

              <div style={{ paddingTop: "20px" }}>
                <Button
                  variant="primary"
                  type="submit"
                  className="login-btn w-100 mt-3"
                  disabled={loading}
                >
                  {loading ? "Creating account…" : "Sign Up"}
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => signUpWithOAuth("google")}
                  className="w-100 mt-3 google-sign-up-button"
                >
                  Sign Up With Google
                </Button>
              </div>
              <div className="text-center mt-3">
                <a href="/login">Already have an Account? Sign In</a>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default SignUp;
